import { HttpErrorResponse } from '@angular/common/http';
import { Component, computed, effect, input, untracked } from '@angular/core';

import { TranslatePipe } from '@camelot/translation';
import { ErrorComponent, LoaderComponent, TitleComponent } from '@camelot/ui';
import { RequestState } from '@camelot/utils';

import { IFieldsOverrides, ITreeData } from '../../models/types';
import { GridRow } from '../../services/dto/grid-row';
import { GridMetaData } from '../../services/dto/metadata';
import { CamBaseGridService } from '../../services/grid-base.service';
import { AgGridComponent } from '../ag-grid/ag-grid.component';
import { AgGridEditabledComponent } from '../ag-grid/editabled/ag-grid-editabled.component';
import { AgGridFooterComponent } from '../ag-grid/footer/ag-grid-footer.component';
import { GridControlComponent } from '../grid-control/grid-control.component';
import { GridExpandableComponent } from '../grid-expandable/grid-expandable.component';
import { GridTagComponent } from '../grid-tag/grid-tag.component';

const autoGroupColumnDef = {
  innerRenderer: (params: { data?: GridRow }) => params.data?.order ?? '',
  name: 'app.grid.editabled.column.ref',
  pinned: 'left' as const,
  size: 120,
};

/** Arbre des lignes editables, par `compositionPath`. */
export const editableGridRowTreeData = <T extends GridRow>(): ITreeData<T> => ({
  autoGroupColumnDef,
  clientSide: { getDataPath: row => row.compositionPath },
});

/** Arbre des lignes en lecture, charge niveau par niveau. */
export const readOnlyGridRowTreeData = <T extends GridRow>(): ITreeData<T> => ({
  autoGroupColumnDef,
  serverSide: { getServerSideGroupKey: row => row.id, isServerSideGroup: row => row.hasChildren },
});

@Component({
  host: {
    '[class.has-fixed-footer]': 'this.isEditable() !== null',
  },
  imports: [
    AgGridComponent,
    AgGridEditabledComponent,
    AgGridFooterComponent,
    ErrorComponent,
    GridControlComponent,
    GridExpandableComponent,
    GridTagComponent,
    LoaderComponent,
    TitleComponent,
    TranslatePipe,
  ],
  selector: 'cam-grid-lines',
  styleUrl: './grid-lines.component.scss',
  templateUrl: './grid-lines.component.html',
})
export class GridLinesComponent<T extends GridRow> {
  title = input<string>('');

  gridId = input.required<string>();

  classicGridId = input.required<string>();

  gridService = input.required<CamBaseGridService<T>>();

  entityId = input.required<string>();

  /** `null` tant que le mode n'est pas connu : aucune des deux grilles n'est montée. */
  isEditable = input<boolean | null>(null);

  colsMetaData = input<GridMetaData[] | null>(null);

  fieldsOverrides = input<IFieldsOverrides>({ description: { flex: 1, pinned: 'left' } });

  public readonly requestState = new RequestState();

  public readonly editable = computed(() => this.gridService().editableDataService(this.entityId()));
  public readonly footer = computed(() => this.gridService().footerDataService(this.entityId()));
  public readonly classic = computed(() => this.gridService().readOnlyDataService(this.entityId()));

  public readonly treeData = editableGridRowTreeData<T>();
  public readonly classicTreeData = readOnlyGridRowTreeData<T>();

  constructor() {
    // la grille en lecture lit le cache `gridRows` : il faut le charger avant de la monter
    effect(onCleanup => {
      const entityId = this.entityId();
      const service = this.gridService();
      if (this.isEditable() !== false || !entityId) {
        return;
      }

      untracked(() => this.requestState.asked());
      const subscription = service.fetchGridRows$(entityId).subscribe({
        complete: () => this.requestState.completed(),
        error: (error: HttpErrorResponse) => this.requestState.onError(error.status, error.statusText),
      });
      onCleanup(() => subscription.unsubscribe());
    });
  }
}
